import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Badge } from "../components/ui/badge";
import { 
  FileText, Mic, ArrowLeft, Loader2, 
  MicOff, Sparkles, Search, Calculator, Package, Coffee, Wrench, CheckCircle,
  HelpCircle, ShieldCheck, ChevronRight, Plus, X, PoundSterling, TrendingUp, TrendingDown,
  AlertCircle, Mail, Phone
} from "lucide-react";
import { toast } from "sonner";
import { useReactMediaRecorder } from "react-media-recorder";

// Common email domains and their common misspellings
const COMMON_EMAIL_DOMAINS = {
  "gmail.com": ["gmial.com", "gmal.com", "gmai.com", "gamil.com", "gmail.co", "gmaill.com", "gmali.com", "gnail.com", "gimail.com", "gmail.con", "gmail.cm", "gmail.om"],
  "hotmail.com": ["hotmial.com", "hotmal.com", "hotmai.com", "hotamil.com", "hotmail.co", "hotmaill.com", "hotmali.com", "hotmil.com", "hotmail.con", "hotmail.cm"],
  "outlook.com": ["outlok.com", "outloo.com", "outlokk.com", "outllook.com", "outlook.co", "outlook.con", "outlool.com", "outook.com"],
  "yahoo.com": ["yaho.com", "yahooo.com", "yaoo.com", "yhoo.com", "yahoo.co", "yahoo.con", "yahho.com", "yahooo.com"],
  "icloud.com": ["icoud.com", "iclod.com", "icload.com", "icloud.co", "icloud.con", "iclooud.com", "iclould.com"],
  "live.com": ["liv.com", "livee.com", "live.co", "live.con"],
  "btinternet.com": ["btinternett.com", "btinternet.co", "btintrnet.com", "btintenet.com"],
  "sky.com": ["skyy.com", "ski.com", "sky.co"],
  "virginmedia.com": ["virginmedia.co", "virgimedia.com", "virginmeida.com"],
  "talktalk.net": ["talktalk.com", "talktak.net", "talktalk.nt"],
  "aol.com": ["aol.co", "aoll.com", "ao.com", "aol.con"],
  "msn.com": ["msn.co", "msnn.com", "msn.con"],
  "mail.com": ["mai.com", "maill.com", "mail.co", "mail.con"],
  "protonmail.com": ["protonmal.com", "protonmail.co", "protonmai.com", "protonmial.com"],
  "co.uk": ["co.u", "co.k", "c.uk", "co.ik", "co.uj"]
};

// Check for common domain typos
const checkDomainTypo = (email) => {
  if (!email || !email.includes("@")) return null;
  
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return null;
  
  // Check each common domain for typos
  for (const [correctDomain, typos] of Object.entries(COMMON_EMAIL_DOMAINS)) {
    if (typos.includes(domain)) {
      return {
        typo: domain,
        suggestion: correctDomain,
        message: `Did you mean @${correctDomain}? 🤔`
      };
    }
  }
  
  // Check for .con instead of .com
  if (domain.endsWith(".con")) {
    return {
      typo: domain,
      suggestion: domain.replace(".con", ".com"),
      message: `Looks like .con instead of .com! Easy typo 😅`
    };
  }
  
  // Check for missing 'm' in .com
  if (domain.endsWith(".co") && !domain.endsWith(".co.uk")) {
    return {
      typo: domain,
      suggestion: domain + "m",
      message: `Missing the 'm' in .com? 🔤`
    };
  }
  
  // Check for .cm instead of .com
  if (domain.endsWith(".cm")) {
    return {
      typo: domain,
      suggestion: domain.replace(".cm", ".com"),
      message: `Did you mean .com instead of .cm? 🌍`
    };
  }
  
  return null;
};

// Email validation with fun messages
const validateEmail = (email) => {
  if (!email) return null;
  
  // First check for domain typos (higher priority)
  const domainCheck = checkDomainTypo(email);
  if (domainCheck) {
    return { 
      valid: false, 
      message: domainCheck.message,
      suggestion: email.split("@")[0] + "@" + domainCheck.suggestion
    };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email.includes("@")) {
    return { valid: false, message: "Hmm, that email's missing an @ symbol! 📧" };
  }
  if (!email.includes(".")) {
    return { valid: false, message: "Looks like you forgot the dot! (.com, .co.uk, etc.) 🔴" };
  }
  if (!emailRegex.test(email)) {
    return { valid: false, message: "That email looks a bit wonky... double-check it? 🤔" };
  }
  return { valid: true, message: null };
};

// Phone validation with fun messages
const validatePhone = (phone) => {
  if (!phone) return null;
  
  // Remove spaces, dashes, parentheses for checking
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, "");
  
  // Check if it's all digits (with optional + at start)
  if (!/^\+?\d*$/.test(cleaned)) {
    return { valid: false, message: "That's got some funky characters in it! Just numbers please 🔢" };
  }
  
  // UK mobile: starts with 07, should be 11 digits
  if (cleaned.startsWith("07") && cleaned.length !== 11) {
    return { valid: false, message: `UK mobiles are 11 digits - you've got ${cleaned.length}! 📱` };
  }
  
  // UK landline: starts with 01 or 02, should be 10-11 digits  
  if ((cleaned.startsWith("01") || cleaned.startsWith("02")) && (cleaned.length < 10 || cleaned.length > 11)) {
    return { valid: false, message: `That landline looks ${cleaned.length < 10 ? "short" : "long"}... might want to check it 📞` };
  }
  
  // International: starts with +, should be 10-15 digits
  if (cleaned.startsWith("+") && (cleaned.length < 10 || cleaned.length > 16)) {
    return { valid: false, message: "International numbers are usually 10-15 digits 🌍" };
  }
  
  // Generic check: should be at least 10 digits for any real number
  if (cleaned.length < 10 && !cleaned.startsWith("+")) {
    return { valid: false, message: "That seems a bit short for a phone number... 🤏" };
  }
  
  if (cleaned.length > 15) {
    return { valid: false, message: "That's quite a lot of digits! Sure it's right? 📏" };
  }
  
  return { valid: true, message: null };
};

// Validation hint component
const ValidationHint = ({ validation, fieldName, onFix }) => {
  if (!validation || validation.valid) return null;
  
  return (
    <div className="flex items-start gap-2 mt-1.5 p-2 bg-amber-500/10 rounded-md border border-amber-500/20 animate-in slide-in-from-top-1 duration-200">
      <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-xs text-amber-400">{validation.message}</p>
        {validation.suggestion && onFix && (
          <button
            type="button"
            onClick={() => onFix(validation.suggestion)}
            className="mt-1 text-xs text-primary hover:underline font-medium"
          >
            Click to use: {validation.suggestion}
          </button>
        )}
      </div>
    </div>
  );
};

// Location qualifiers that should NOT trigger job detection
const LOCATION_QUALIFIERS = [
  "small bathroom", "large bathroom", "main bathroom", "en-suite", "ensuite",
  "downstairs bathroom", "upstairs bathroom", "guest bathroom",
  "small room", "large room", "bedroom", "living room", "lounge",
  "kitchen area", "utility room", "garage", "loft", "attic", "basement", "cellar",
  "conservatory", "extension", "annex", "outbuilding", "ground floor", "first floor"
];

// Clean description by removing location qualifiers
const cleanDescriptionForDisplay = (description) => {
  let cleaned = description;
  for (const qualifier of LOCATION_QUALIFIERS) {
    cleaned = cleaned.replace(new RegExp(`\\(${qualifier}\\)`, 'gi'), "");
  }
  return cleaned.trim();
};

// Fun loading messages for quote generation
const LOADING_STAGES = [
  { icon: "Search", message: "Analyzing your job description...", tip: "Time for a sip of that cold brew you have been ignoring ☕" },
  { icon: "Package", message: "Identifying materials needed...", tip: "Did you remember to lock the van? 🚐" },
  { icon: "Search", message: "Searching Screwfix for the best prices...", tip: "Their website is slower than a Monday morning 😴" },
  { icon: "Calculator", message: "Comparing prices across suppliers...", tip: "Finding you the mid-range sweet spot 💰" },
  { icon: "Wrench", message: "Calculating labor hours...", tip: "We factor in tea breaks, obviously 🍵" },
  { icon: "Calculator", message: "Adding your margin...", tip: "Gotta keep the lights on innit 💡" },
  { icon: "CheckCircle", message: "Finalizing your quote...", tip: "Almost there, champion! 🏆" },
];

const LoadingOverlay = ({ currentStage }) => {
  const stage = LOADING_STAGES[currentStage] || LOADING_STAGES[0];
  
  const IconComponent = {
    Search: Search,
    Package: Package,
    Calculator: Calculator,
    Wrench: Wrench,
    CheckCircle: CheckCircle,
    Coffee: Coffee,
  }[stage.icon] || Loader2;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="max-w-md w-full mx-4">
        <Card className="bg-card border-primary/20 shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            {/* Animated icon */}
            <div className="relative mx-auto w-20 h-20 mb-6">
              <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-primary">
                <IconComponent className="w-10 h-10 text-primary animate-pulse" />
              </div>
            </div>
            
            {/* Main message */}
            <h3 className="text-xl font-bold text-foreground mb-2">
              {stage.message}
            </h3>
            
            {/* Fun tip */}
            <p className="text-sm text-muted-foreground mb-6">
              {stage.tip}
            </p>
            
            {/* Progress bar */}
            <div className="w-full bg-secondary rounded-full h-2 mb-4">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((currentStage + 1) / LOADING_STAGES.length) * 100}%` }}
              />
            </div>
            
            {/* Stage indicator */}
            <div className="flex justify-center gap-2">
              {LOADING_STAGES.map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    idx <= currentStage ? 'bg-primary' : 'bg-secondary'
                  }`}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Dynamic Smart Questions Modal - fetches scoping questions from backend
const SmartQuestionsModal = ({ 
  jobsWithQuestions,  // From backend /jobs/scoping-questions
  answers, 
  setAnswers, 
  complexity,
  setComplexity,
  onConfirm, 
  onSkip 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  // Flatten all questions across all jobs
  const allQuestions = jobsWithQuestions.flatMap(job => 
    job.questions.map(q => ({
      ...q,
      jobName: job.job_name,
      jobKey: job.job_key,
      category: job.category,
      priceRange: job.price_range,
      key: `${job.job_key}_${q.id}`
    }))
  );
  
  // Always show complexity selection as the last step
  const showComplexityStep = true; // Always ask for complexity
  const totalSteps = allQuestions.length + 1; // Questions + complexity
  const isOnComplexityStep = currentStep >= allQuestions.length;
  const currentQuestion = allQuestions[currentStep];
  
  const handleAnswerSelect = (answer) => {
    if (currentQuestion) {
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.key]: answer
      }));
      // Auto-advance
      setTimeout(() => {
        if (currentStep < totalSteps - 1) {
          setCurrentStep(prev => prev + 1);
        }
      }, 300);
    }
  };
  
  const handleComplexitySelect = (value) => {
    setComplexity(value);
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="max-w-lg w-full mx-4">
        <Card className="bg-card border-primary/20 shadow-2xl overflow-hidden">
          {/* Progress bar */}
          <div className="h-1 bg-secondary">
            <div 
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
          
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="font-medium text-primary">{currentStep + 1}</span>
                <span>of {totalSteps}</span>
              </div>
              {jobsWithQuestions.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  {jobsWithQuestions.map(j => j.job_name).join(" + ")}
                </Badge>
              )}
            </div>
            <CardTitle className="text-xl mt-2">
              {isOnComplexityStep ? "Job Complexity" : currentQuestion?.question}
            </CardTitle>
            {!isOnComplexityStep && currentQuestion && (
              <p className="text-sm text-muted-foreground mt-1">
                For: {currentQuestion.jobName}
              </p>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            {isOnComplexityStep ? (
              // Complexity Selection (no prices shown)
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-4">
                  Select the job complexity level:
                </p>
                
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    complexity === "low" 
                      ? "border-green-500 bg-green-500/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}
                  onClick={() => handleComplexitySelect("low")}
                  data-testid="complexity-low"
                >
                  <div className="flex items-center gap-3">
                    <TrendingDown className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium">Basic / Simple</p>
                      <p className="text-sm text-muted-foreground">Easy access, standard parts, straightforward job</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    complexity === "mid" 
                      ? "border-primary bg-primary/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}
                  onClick={() => handleComplexitySelect("mid")}
                  data-testid="complexity-mid"
                >
                  <div className="flex items-center gap-3">
                    <PoundSterling className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium">Standard / Typical</p>
                      <p className="text-sm text-muted-foreground">Average job, normal conditions</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    complexity === "high" 
                      ? "border-orange-500 bg-orange-500/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}
                  onClick={() => handleComplexitySelect("high")}
                  data-testid="complexity-high"
                >
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="font-medium">Complex / Difficult</p>
                      <p className="text-sm text-muted-foreground">Access issues, premium parts, complications</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Scoping Question - different types
              <div className="space-y-3">
                {currentQuestion?.type === "boolean" ? (
                  // Yes/No question (for tiling)
                  <div className="space-y-3">
                    <div 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        answers[currentQuestion?.key] === true
                          ? "border-green-500 bg-green-500/10" 
                          : "border-white/10 hover:border-white/30"
                      }`}
                      onClick={() => {
                        setAnswers(prev => ({
                          ...prev,
                          [currentQuestion?.key]: true
                        }));
                      }}
                      data-testid="answer-yes"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <p className="font-medium">Yes</p>
                      </div>
                    </div>
                    
                    <div 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        answers[currentQuestion?.key] === false
                          ? "border-primary bg-primary/10" 
                          : "border-white/10 hover:border-white/30"
                      }`}
                      onClick={() => {
                        setAnswers(prev => ({
                          ...prev,
                          [currentQuestion?.key]: false
                        }));
                      }}
                      data-testid="answer-no"
                    >
                      <div className="flex items-center gap-3">
                        <X className="w-5 h-5 text-muted-foreground" />
                        <p className="font-medium">No</p>
                      </div>
                    </div>
                    
                    {/* Follow-up question if user selects Yes */}
                    {answers[currentQuestion?.key] === true && currentQuestion?.follow_up && (
                      <div className="mt-4 p-4 rounded-lg bg-primary/5 border border-primary/20 animate-in slide-in-from-top-2">
                        <Label className="text-sm font-medium">{currentQuestion.follow_up.question.question}</Label>
                        {currentQuestion.follow_up.question.hint && (
                          <p className="text-xs text-muted-foreground mt-1">{currentQuestion.follow_up.question.hint}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            type="number"
                            placeholder={currentQuestion.follow_up.question.placeholder || "Enter value"}
                            value={answers[currentQuestion.follow_up.question.id] || ""}
                            onChange={(e) => setAnswers(prev => ({
                              ...prev,
                              [currentQuestion.follow_up.question.id]: e.target.value
                            }))}
                            className="bg-input/50 border-white/10 w-32"
                            data-testid="follow-up-input"
                          />
                          {currentQuestion.follow_up.question.unit && (
                            <span className="text-sm text-muted-foreground">{currentQuestion.follow_up.question.unit}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : currentQuestion?.type === "number" ? (
                  // Numeric input (for sqm)
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder={currentQuestion?.placeholder || "Enter value"}
                        value={answers[currentQuestion?.key] || ""}
                        onChange={(e) => setAnswers(prev => ({
                          ...prev,
                          [currentQuestion?.key]: e.target.value
                        }))}
                        className="bg-input/50 border-white/10 w-40"
                        data-testid="number-input"
                      />
                      {currentQuestion?.unit && (
                        <span className="text-sm text-muted-foreground">{currentQuestion.unit}</span>
                      )}
                    </div>
                    {currentQuestion?.hint && (
                      <p className="text-xs text-muted-foreground">{currentQuestion.hint}</p>
                    )}
                  </div>
                ) : currentQuestion?.type === "select" ? (
                  // Select options
                  <div className="space-y-2">
                    {currentQuestion?.options?.map((option, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          answers[currentQuestion?.key] === option
                            ? "border-primary bg-primary/10" 
                            : "border-white/10 hover:border-white/30"
                        }`}
                        onClick={() => {
                          setAnswers(prev => ({
                            ...prev,
                            [currentQuestion?.key]: option
                          }));
                        }}
                        data-testid={`select-option-${idx}`}
                      >
                        <p className="font-medium">{option}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Default: free-form text answer
                  <Textarea
                    placeholder="Type your answer..."
                    value={answers[currentQuestion?.key] || ""}
                    onChange={(e) => setAnswers(prev => ({
                      ...prev,
                      [currentQuestion?.key]: e.target.value
                    }))}
                    className="min-h-[100px] bg-input/50 border-white/10"
                    data-testid="scoping-answer-input"
                  />
                )}
                
                <p className="text-xs text-muted-foreground mt-2">
                  Your answer helps us provide a more accurate quote
                </p>
              </div>
            )}
            
            {/* Navigation */}
            <div className="flex gap-2 pt-4">
              {currentStep > 0 && (
                <Button variant="ghost" onClick={goBack} className="flex-none">
                  Back
                </Button>
              )}
              
              <Button 
                variant="ghost" 
                onClick={onSkip}
                className="text-muted-foreground"
              >
                Skip Questions
              </Button>
              
              <div className="flex-1" />
              
              {isOnComplexityStep ? (
                <Button
                  onClick={onConfirm}
                  disabled={!complexity}
                  className="flex-1 bg-primary hover:bg-primary/90"
                  data-testid="generate-quote-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Quote
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={goNext}
                  className="flex-1"
                  disabled={!answers[currentQuestion?.key]}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Duplicate modal code removed


const NewQuote = ({ user, API }) => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState("text");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  
  // Smart questions state - now using backend data
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [jobsWithQuestions, setJobsWithQuestions] = useState([]);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [complexity, setComplexity] = useState("mid"); // low, mid, high
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  
  // Form state
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  
  // Voice recording
  const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } = useReactMediaRecorder({
    audio: true,
    blobPropertyBag: { type: "audio/webm" }
  });
  
  const isRecording = status === "recording";
  
  // Progress through loading stages
  useEffect(() => {
    let interval;
    if (isGenerating) {
      setLoadingStage(0);
      interval = setInterval(() => {
        setLoadingStage(prev => {
          if (prev < LOADING_STAGES.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 2500); // Change message every 2.5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isGenerating]);
  
  // Process voice recording when available
  useEffect(() => {
    if (mediaBlobUrl && !isProcessing) {
      processVoiceRecording();
    }
  }, [mediaBlobUrl]);
  
  const processVoiceRecording = async () => {
    if (!mediaBlobUrl) return;
    
    setIsProcessing(true);
    try {
      const response = await fetch(mediaBlobUrl);
      const blob = await response.blob();
      
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        const transcribeResponse = await fetch(`${API}/ai/transcribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ audio_base64: base64 })
        });
        
        if (!transcribeResponse.ok) {
          throw new Error("Transcription failed");
        }
        
        const data = await transcribeResponse.json();
        setJobDescription(prev => prev + (prev ? " " : "") + data.text);
        toast.success("Voice transcribed successfully");
        clearBlobUrl();
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      toast.error("Failed to transcribe voice: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle initial button click - fetch scoping questions from backend
  const handleGenerateClick = async () => {
    if (!customerName.trim()) {
      toast.error("Please enter customer name");
      return;
    }
    if (!jobDescription.trim()) {
      toast.error("Please describe the job");
      return;
    }
    
    setIsLoadingQuestions(true);
    
    try {
      // Call backend to get scoping questions for this job description
      const response = await fetch(`${API}/jobs/scoping-questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          description: jobDescription
        })
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch scoping questions");
      }
      
      const data = await response.json();
      
      // Also fetch smart questions (for tiling, UFH, etc.)
      try {
        const smartResponse = await fetch(`${API}/ai/smart-questions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ description: jobDescription })
        });
        
        if (smartResponse.ok) {
          const smartData = await smartResponse.json();
          if (smartData.has_questions) {
            // Convert smart questions to the same format as scoping questions
            const smartJobQuestions = {
              job_key: "smart_questions",
              job_name: "Additional Details",
              category: "Smart Questions",
              price_range: null,
              questions: smartData.questions.map(q => ({
                id: q.id,
                question: q.question,
                type: q.type,
                options: q.options,
                follow_up: q.follow_up,
                hint: q.hint,
                unit: q.unit,
                placeholder: q.placeholder
              }))
            };
            // Add smart questions to the beginning
            setJobsWithQuestions([smartJobQuestions, ...(data.jobs_with_questions || [])]);
          } else {
            setJobsWithQuestions(data.jobs_with_questions || []);
          }
        } else {
          setJobsWithQuestions(data.jobs_with_questions || []);
        }
      } catch (smartErr) {
        console.log("Smart questions not available:", smartErr);
        setJobsWithQuestions(data.jobs_with_questions || []);
      }
      
      setShowQuestionnaire(true);
    } catch (error) {
      console.error("Error fetching scoping questions:", error);
      // Still show complexity question even on error
      setJobsWithQuestions([]);
      setShowQuestionnaire(true);
    } finally {
      setIsLoadingQuestions(false);
    }
  };
  
  // Build enhanced description with questionnaire answers
  const buildEnhancedDescription = () => {
    let enhanced = jobDescription;
    
    // Add answers from questionnaire
    if (Object.keys(questionAnswers).length > 0) {
      enhanced += "\n\n--- Scoping Details ---";
      jobsWithQuestions.forEach(job => {
        enhanced += `\n\n${job.job_name}:`;
        job.questions.forEach(q => {
          const answer = questionAnswers[`${job.job_key}_${q.id}`];
          if (answer) {
            enhanced += `\n- ${q.question}: ${answer}`;
          }
        });
      });
    }
    
    // Add complexity level (always include)
    if (complexity) {
      enhanced += `\n\n--- Job Complexity ---\nComplexity Level: ${complexity}`;
    }
    
    return enhanced;
  };
  
  const generateQuote = async () => {
    setShowQuestionnaire(false);
    setIsGenerating(true);
    setLoadingStage(0);
    
    try {
      const enhancedDescription = buildEnhancedDescription();
      
      // Build questionnaire_answers object for backend
      const questionnaireAnswers = {};
      Object.entries(questionAnswers).forEach(([key, value]) => {
        // Extract the actual question id (remove job_key prefix for smart questions)
        if (key.startsWith("smart_questions_")) {
          const actualKey = key.replace("smart_questions_", "");
          questionnaireAnswers[actualKey] = value;
        } else {
          questionnaireAnswers[key] = value;
        }
      });
      
      const response = await fetch(`${API}/ai/generate-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          description: enhancedDescription,
          customer_name: customerName,
          customer_email: customerEmail || null,
          customer_phone: customerPhone || null,
          customer_address: customerAddress || null,
          questionnaire_answers: Object.keys(questionnaireAnswers).length > 0 ? questionnaireAnswers : null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle quota exceeded specifically
        if (response.status === 403 && errorData?.detail?.error === "quota_exceeded") {
          const quotaInfo = errorData.detail;
          toast.error(
            `You've used all your quote credits. ${quotaInfo.can_borrow ? "Borrow from next month or " : ""}Purchase more credits to continue.`,
            {
              duration: 6000,
              action: {
                label: "Get Credits",
                onClick: () => navigate("/pricing")
              }
            }
          );
          setIsGenerating(false);
          return;
        }
        
        throw new Error(errorData?.detail || "Quote generation failed");
      }
      
      const quote = await response.json();
      toast.success("Quote generated successfully");
      navigate(`/quote/${quote.quote_id}`);
    } catch (error) {
      toast.error("Failed to generate quote: " + error.message);
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <>
      {/* Loading Overlay */}
      {isGenerating && <LoadingOverlay currentStage={loadingStage} />}
      
      {/* Smart Questions Modal */}
      {showQuestionnaire && (
        <SmartQuestionsModal
          jobsWithQuestions={jobsWithQuestions}
          answers={questionAnswers}
          setAnswers={setQuestionAnswers}
          complexity={complexity}
          setComplexity={setComplexity}
          onConfirm={generateQuote}
          onSkip={generateQuote}
        />
      )}
      
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold">New Quote</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="space-y-6">
          {/* Customer Info */}
          <Card className="bg-card border-white/5">
            <CardHeader>
              <CardTitle className="text-lg">Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customerName">Customer Name *</Label>
                  <Input
                    id="customerName"
                    data-testid="customer-name-input"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="John Smith"
                    className="bg-input/50 border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customerEmail" className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" />
                    Email
                  </Label>
                  <Input
                    id="customerEmail"
                    data-testid="customer-email-input"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="john@example.com"
                    className={`bg-input/50 border-white/10 ${
                      customerEmail && !validateEmail(customerEmail)?.valid 
                        ? "border-amber-500/50 focus:border-amber-500" 
                        : ""
                    }`}
                  />
                  <ValidationHint 
                    validation={validateEmail(customerEmail)} 
                    fieldName="email"
                    onFix={(suggestion) => setCustomerEmail(suggestion)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customerPhone" className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5" />
                    Phone
                  </Label>
                  <Input
                    id="customerPhone"
                    data-testid="customer-phone-input"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="07123 456789"
                    className={`bg-input/50 border-white/10 ${
                      customerPhone && !validatePhone(customerPhone)?.valid 
                        ? "border-amber-500/50 focus:border-amber-500" 
                        : ""
                    }`}
                  />
                  <ValidationHint validation={validatePhone(customerPhone)} fieldName="phone" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customerAddress">Address</Label>
                  <Input
                    id="customerAddress"
                    data-testid="customer-address-input"
                    value={customerAddress}
                    onChange={(e) => setCustomerAddress(e.target.value)}
                    placeholder="123 Main Street..."
                    className="bg-input/50 border-white/10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Job Description Input */}
          <Card className="bg-card border-white/5">
            <CardHeader>
              <CardTitle className="text-lg">Job Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-2 w-full bg-secondary">
                  <TabsTrigger 
                    value="text" 
                    data-testid="tab-text"
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Text
                  </TabsTrigger>
                  <TabsTrigger 
                    value="voice" 
                    data-testid="tab-voice"
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Voice
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="text" className="mt-4">
                  <div className="space-y-2">
                    <Textarea
                      data-testid="job-description-input"
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      placeholder="Describe the plumbing work needed...&#10;&#10;Example: Replace leaking tap in kitchen, install new mixer tap. Customer reports dripping for 2 weeks. Need to check shut-off valves."
                      className="min-h-[200px] bg-input/50 border-white/10 resize-none"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="voice" className="mt-4">
                  <div className="text-center py-8">
                    <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center transition-all ${
                      isRecording 
                        ? "bg-destructive recording-pulse" 
                        : "bg-secondary hover:bg-primary/20"
                    }`}>
                      {isProcessing ? (
                        <Loader2 className="w-10 h-10 animate-spin text-primary" />
                      ) : isRecording ? (
                        <MicOff className="w-10 h-10 text-white" />
                      ) : (
                        <Mic className="w-10 h-10 text-primary" />
                      )}
                    </div>
                    
                    {isProcessing ? (
                      <p className="text-muted-foreground">Processing recording...</p>
                    ) : isRecording ? (
                      <>
                        <p className="text-destructive font-medium mb-4">Recording...</p>
                        <Button
                          data-testid="stop-recording-btn"
                          onClick={stopRecording}
                          variant="destructive"
                          size="lg"
                        >
                          <MicOff className="w-5 h-5 mr-2" />
                          Stop Recording
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-muted-foreground mb-4">
                          Press to record your job description
                        </p>
                        <Button
                          data-testid="start-recording-btn"
                          onClick={startRecording}
                          size="lg"
                          className="bg-primary text-primary-foreground"
                        >
                          <Mic className="w-5 h-5 mr-2" />
                          Start Recording
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {jobDescription && (
                    <div className="mt-4 p-4 bg-secondary/30 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Transcribed text:</p>
                      <p>{jobDescription}</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Generate Button */}
          <Button
            onClick={handleGenerateClick}
            disabled={isGenerating || showQuestionnaire || isLoadingQuestions || !customerName || !jobDescription}
            size="lg"
            className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
            data-testid="generate-quote-btn"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                Generating Quote...
              </>
            ) : isLoadingQuestions ? (
              <>
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                Loading Questions...
              </>
            ) : (
              <>
                <Sparkles className="w-6 h-6 mr-3" />
                Generate Quote with AI
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
    </>
  );
};

export default NewQuote;
